import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, ShieldOff, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const { user } = useAuth();
  const [qrData, setQrData] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [verified, setVerified] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Fetch latest totp_enabled status on mount
  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then(res => setTotpEnabled(res.data?.totp_enabled || false))
      .catch(() => {})
      .finally(() => setInitialLoading(false));
  }, []);

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/totp/setup`);
      setQrData(res.data);
      setVerified(false);
      setCode("");
    } catch { toast.error("Failed to generate QR code"); }
    finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (code.length !== 6) { toast.error("Enter a 6-digit code"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/totp/verify`, { code });
      setTotpEnabled(true);
      setVerified(true);
      setQrData(null);
      toast.success("2FA enabled successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setLoading(false); }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/totp/disable`);
      setTotpEnabled(false);
      setQrData(null);
      setVerified(false);
      toast.success("2FA disabled");
    } catch { toast.error("Failed to disable 2FA"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="admin-settings">
      <h1 className="font-outfit text-xl font-bold text-white">Security Settings</h1>

      {initialLoading ? (
        <div className="text-sarathi-text-muted text-center py-10">Loading...</div>
      ) : (
      <div className="bg-sarathi-surface border border-white/10 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className={`w-6 h-6 ${totpEnabled ? "text-green-400" : "text-sarathi-text-muted"}`} />
            <div>
              <h2 className="text-white font-semibold text-sm">Two-Factor Authentication</h2>
              <p className="text-sarathi-text-muted text-xs mt-0.5">
                {totpEnabled ? "Enabled — your account is protected" : "Not enabled — add an extra layer of security"}
              </p>
            </div>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full border ${totpEnabled ? "bg-green-500/15 text-green-400 border-green-500/30" : "bg-orange-500/15 text-orange-400 border-orange-500/30"}`}>
            {totpEnabled ? "Active" : "Inactive"}
          </span>
        </div>

        {!totpEnabled && !qrData && (
          <Button onClick={handleSetup} disabled={loading} className="bg-sarathi-primary hover:bg-sarathi-primary-hover text-white h-10 text-sm" data-testid="setup-2fa-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set Up 2FA"}
          </Button>
        )}

        {qrData && !verified && (
          <div className="space-y-4 mt-4 pt-4 border-t border-white/5">
            <div className="text-center">
              <p className="text-white text-sm font-medium mb-3">Scan this QR code with Google Authenticator</p>
              <div className="inline-block bg-white rounded-xl p-3">
                <img src={`data:image/png;base64,${qrData.qr_code}`} alt="TOTP QR Code" className="w-48 h-48" data-testid="totp-qr" />
              </div>
              <p className="text-sarathi-text-muted text-xs mt-3">Or enter this key manually:</p>
              <code className="text-sarathi-primary text-xs font-mono bg-sarathi-background px-3 py-1.5 rounded-lg inline-block mt-1" data-testid="totp-secret">
                {qrData.secret}
              </code>
            </div>

            <div>
              <label className="block text-sarathi-text-muted text-sm mb-1.5">Enter the 6-digit code to verify</label>
              <div className="flex gap-2">
                <Input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000" maxLength={6}
                  className="bg-sarathi-background border-white/10 text-white rounded-xl h-11 text-center text-xl font-mono tracking-[0.3em] flex-1" data-testid="totp-verify-input" />
                <Button onClick={handleVerify} disabled={loading || code.length !== 6} className="bg-green-600 hover:bg-green-700 text-white h-11 px-6" data-testid="totp-verify-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Verify</>}
                </Button>
              </div>
            </div>
          </div>
        )}

        {totpEnabled && (
          <Button onClick={handleDisable} disabled={loading} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-9 text-sm mt-2" data-testid="disable-2fa-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldOff className="w-4 h-4 mr-1" /> Disable 2FA</>}
          </Button>
        )}
      </div>
      )}
    </div>
  );
}
